/**
 * Helpers to derive display context from packing tasks at runtime.
 * Used for destination-based grouping and trailer context.
 */

import type { PackingOperation, PackingTask } from '../schema';
import type { ItemType } from '../schema';

/** Packing list item shape needed for trailer detection (itemType with category) */
export interface PackingListItemLike {
    itemType?: {
        id: string;
        name?: string;
        category?: { name: string } | null;
    } | null;
}

/** One destination group for task list display */
export interface TaskGroup {
    eventId: string;
    eventName: string;
    trailers: ItemType[];
    tasks: PackingTask[];
}

/**
 * Get trailer item types that should be at a given destination from the operation's event data.
 * Used during completion to show trailer context per destination.
 */
export function getTrailerItemTypesAtEvent(operation: PackingOperation, eventId: string): ItemType[] {
    const events = [...(operation.targetEvents || []), ...(operation.sourceEvents || [])];
    const event = events.find((e: { id?: string }) => e.id === eventId);
    if (!event) return [];

    const packingListItems = (event as { packingListItems?: PackingListItemLike[] }).packingListItems || [];
    const seen = new Set<string>();
    const trailers: ItemType[] = [];

    for (const item of packingListItems) {
        const it = item.itemType;
        if (!it?.id) continue;
        const categoryName = it.category?.name ?? '';
        if (categoryName === 'Trailers' && !seen.has(it.id)) {
            seen.add(it.id);
            trailers.push(it as ItemType);
        }
    }

    return trailers;
}

/**
 * Infer which trailer(s) are associated with a task's destination from the event's packing list.
 */
export function getTrailersForDestination(task: PackingTask, packingLists: Map<string, PackingListItemLike[]>): ItemType[] {
    const toId = task.toEvent?.id;
    if (!toId) return [];

    const packingList = packingLists.get(toId) || [];
    const seen = new Set<string>();
    const trailers: ItemType[] = [];

    for (const item of packingList) {
        const it = item.itemType;
        if (!it?.id) continue;
        const categoryName = it.category?.name ?? '';
        if (categoryName === 'Trailers' && !seen.has(it.id)) {
            seen.add(it.id);
            trailers.push(it as ItemType);
        }
    }

    return trailers;
}

/**
 * Group tasks by destination for display (primary organization).
 * Each group has eventId, eventName, trailers, and tasks.
 */
export function groupTasksByDestination(
    tasks: PackingTask[],
    _operation: PackingOperation,
    packingLists: Map<string, PackingListItemLike[]>
): Map<string, TaskGroup> {
    const groups = new Map<string, TaskGroup>();

    for (const task of tasks) {
        const destId = task.toEvent?.id ?? task.toStorageLocation?.id ?? 'storage';
        const eventName = task.toEvent?.name ?? task.toStorageLocation?.name ?? 'Storage';

        let group = groups.get(destId);
        if (!group) {
            group = {
                eventId: destId,
                eventName,
                trailers: getTrailersForDestination(task, packingLists),
                tasks: [],
            };
            groups.set(destId, group);
        }
        group.tasks.push(task);
    }

    return groups;
}
